import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../Header';
import EarningsHeader from './EarningsHeader';
import EarningsDateGroup from './EarningsDateGroup';
import EarningsHistogram from './EarningsHistogram';
import EarningsDayPanel from './EarningsDayPanel';
import { useEarningsData } from './useEarningsData';
import { useHistogramNavigation } from './useHistogramNavigation';
import { useStockNews } from './useStockNews';
import { useCallHighlights, useWatchlistData } from '../../services/api';

const SkeletonTile = ({ index }) => (
  <div
    className="bg-white rounded-lg border-l-[3px] border-l-gray-200 animate-pulse"
    style={{ animationDelay: `${index * 60}ms` }}
  >
    <div className="flex items-center gap-2 px-3 py-2">
      <div className="w-6 h-6 rounded bg-gray-100 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <div className="w-10 h-3.5 rounded bg-gray-100" />
          <div className="w-24 h-3 rounded bg-gray-50" />
        </div>
      </div>
      <div className="w-12 h-3 rounded bg-gray-50 flex-shrink-0" />
      <div className="flex-shrink-0 text-right">
        <div className="w-14 h-3.5 rounded bg-gray-100 mb-0.5" />
        <div className="w-10 h-2.5 rounded bg-gray-50" />
      </div>
      <div className="w-4 h-4 rounded bg-gray-50 flex-shrink-0" />
    </div>
  </div>
);

const EarningsPage = ({ loggedInUser, onLoginSuccess, onLogout, onOpenLabelManager }) => {
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [selectedIndustries, setSelectedIndustries] = useState(() => {
    try { return JSON.parse(localStorage.getItem('earningsIndustryFilter')) || []; } catch { return []; }
  });
  const [expandedTicker, setExpandedTicker] = useState(null);
  const bottomRef = useRef(null);
  const observerRef = useRef(null);
  // Tracks whether the user manually toggled All/Watchlist this session, so the
  // "default to Watchlist when logged in" effect doesn't fight an explicit choice.
  const userToggledWatchlistRef = useRef(false);

  const { data: watchlistData } = useWatchlistData(loggedInUser?.email);
  const watchlistSymbols = useMemo(() => {
    if (!showWatchlist || !watchlistData?.companiesSymbols?.length) return null;
    return watchlistData.companiesSymbols;
  }, [showWatchlist, watchlistData?.companiesSymbols]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
  } = useEarningsData(7, watchlistSymbols, selectedIndustries.length ? selectedIndustries : null, 1);

  // Extract all loaded stock symbols for batch news fetch
  const allSymbols = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => (page.stocks || []).map(s => s.symbol));
  }, [data?.pages]);

  const { data: newsMap } = useStockNews(allSymbols);
  const { data: highlightsMap } = useCallHighlights(allSymbols, loggedInUser?.email);

  const handleToggleTicker = useCallback((ticker) => {
    setExpandedTicker((prev) => (prev === ticker ? null : ticker));
  }, []);

  const handleToggleWatchlist = useCallback(() => {
    userToggledWatchlistRef.current = true;
    setShowWatchlist((prev) => !prev);
    setExpandedTicker(null);
  }, []);

  // Default the filter to Watchlist once the user is logged in (unless they've
  // manually toggled it this session). Revert to All + clear the flag on logout.
  useEffect(() => {
    if (loggedInUser && !userToggledWatchlistRef.current) {
      setShowWatchlist(true);
    } else if (!loggedInUser) {
      userToggledWatchlistRef.current = false;
      setShowWatchlist(false);
    }
  }, [loggedInUser]);

  const handleToggleIndustry = useCallback((industry) => {
    setSelectedIndustries((prev) => {
      const next = prev.includes(industry) ? prev.filter(i => i !== industry) : [...prev, industry];
      localStorage.setItem('earningsIndustryFilter', JSON.stringify(next));
      return next;
    });
    setExpandedTicker(null);
  }, []);

  const handleClearIndustries = useCallback(() => {
    setSelectedIndustries([]);
    localStorage.removeItem('earningsIndustryFilter');
    setExpandedTicker(null);
  }, []);

  // Industries from API response (always from first page, unfiltered by industry selection)
  const industries = useMemo(() => {
    return data?.pages?.[0]?.industries || [];
  }, [data?.pages]);

  // Group stocks by date (normalize ISO timestamps to date-only for grouping)
  const dateGroups = useMemo(() => {
    if (!data?.pages) return [];
    const allStocks = data.pages.flatMap((page) => page.stocks || []);
    const groups = {};
    for (const stock of allStocks) {
      const raw = stock.next_earnings_date || 'unknown';
      const dateKey = raw.includes('T') ? raw.split('T')[0] : raw;
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(stock);
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [data?.pages]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    observerRef.current?.disconnect();

    if (!hasNextPage || isFetchingNextPage) return;

    const element = bottomRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          fetchNextPage();
        }
      },
      { root: null, rootMargin: '200px', threshold: 0.1 }
    );
    observer.observe(element);
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, dateGroups.length]);

  const totalCount = data?.pages?.[0]?.total || 0;
  const histogram = data?.pages?.[0]?.histogram || [];

  const { handleBarClick, panelDate, panelBellwethers, closePanel } = useHistogramNavigation({
    data, dateGroups, hasNextPage, isFetchingNextPage, fetchNextPage, histogram,
  });

  return (
    <>
      <Helmet>
        <title>Upcoming Earnings This Week | QuantApe</title>
        <meta name="description" content="Stocks reporting earnings this week with 52-week position, volume trends, technical signals, and historical earnings reactions." />
        <link rel="canonical" href="https://quantape.com/earnings" />
      </Helmet>

      <div className="min-h-screen bg-[#f8f9fb]">
        {/* App header */}
        <div className="container mx-auto mt-4">
          <Header
            onLoginSuccess={onLoginSuccess}
            onLogout={onLogout}
            loggedInUser={loggedInUser}
            appStatus={null}
            onProfileClick={() => {}}
            onNotificationsClick={() => {}}
            onSubscriptionClick={() => {}}
            onSettingsSave={() => {}}
            onPreferencesClick={() => {}}
          />
        </div>

        {/* Page header with filters */}
        <EarningsHeader
          showWatchlist={showWatchlist}
          onToggleWatchlist={handleToggleWatchlist}
          loggedInUser={loggedInUser}
          totalCount={totalCount}
          isLoading={isLoading}
          industries={industries}
          selectedIndustries={selectedIndustries}
          onToggleIndustry={handleToggleIndustry}
          onClearIndustries={handleClearIndustries}
        />

        {/* Content */}
        <div className="w-full md:w-3/4 mx-auto pb-12 pt-4">
          {/* 30-day histogram */}
          {!isLoading && histogram.length > 0 && (
            <EarningsHistogram histogram={histogram} onBarClick={handleBarClick} selectedDate={panelDate} />
          )}

          {/* Day panel for bar clicks beyond the list window */}
          {panelDate && (
            <EarningsDayPanel
              date={panelDate}
              bellwethers={panelBellwethers}
              onClose={closePanel}
              loggedInUser={loggedInUser}
              onLoginSuccess={onLoginSuccess}
              onOpenLabelManager={onOpenLabelManager}
            />
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <div className="px-4 space-y-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonTile key={i} index={i} />
              ))}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && dateGroups.length === 0 && (
            <div className="px-4 py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                <span className="text-2xl">📅</span>
              </div>
              <p className="text-gray-900 font-semibold text-lg">No upcoming earnings</p>
              <p className="text-gray-400 text-sm mt-1">
                {selectedIndustries.length > 0
                  ? 'No stocks in the selected industries report in the next 7 days'
                  : showWatchlist
                    ? 'None of your watchlist stocks report in the next 7 days'
                    : 'No stocks report earnings in the next 7 days'}
              </p>
              {(showWatchlist || selectedIndustries.length > 0) && (
                <button
                  onClick={() => { setShowWatchlist(false); setSelectedIndustries([]); }}
                  className="mt-3 text-sm text-indigo-600 font-semibold hover:text-indigo-700"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Date groups */}
          {dateGroups.map(([date, stocks]) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const target = new Date(date.includes('T') ? date : date + 'T00:00:00');
            const isPast = target < today;
            return (
              <EarningsDateGroup
                key={date}
                date={date}
                stocks={stocks}
                loggedInUser={loggedInUser}
                expandedTicker={expandedTicker}
                onToggleTicker={handleToggleTicker}
                onLoginSuccess={onLoginSuccess}
                onOpenLabelManager={onOpenLabelManager}
                defaultCollapsed={isPast}
                newsMap={newsMap}
                highlightsMap={highlightsMap}
              />
            );
          })}

          {/* Infinite scroll sentinel */}
          {hasNextPage && (
            <div ref={bottomRef} className="px-4 py-6">
              {isFetchingNextPage && (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  <span className="text-xs text-gray-400 font-medium">Loading more...</span>
                </div>
              )}
            </div>
          )}

          {/* Filter change loading indicator */}
          {isFetching && !isLoading && !isFetchingNextPage && (
            <div className="fixed top-20 left-1/2 -translate-x-1/2 z-30">
              <div className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white text-xs font-semibold rounded-full shadow-xl">
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Updating
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EarningsPage;
